import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, body, confirmLabel, tone, resolve }
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts?.title || "Are you sure?",
        body: opts?.body || "",
        confirmLabel: opts?.confirmLabel || "Confirm",
        cancelLabel: opts?.cancelLabel || "Cancel",
        tone: opts?.tone || "danger",
      });
    });
  }, []);

  const close = (val) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (r) r(val);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => close(false)}
          data-testid="confirm-dialog"
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0F0F0F] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 flex items-start gap-3">
              <div className={`shrink-0 w-9 h-9 rounded-full grid place-items-center border ${state.tone === "danger" ? "bg-red-500/10 border-red-500/40 text-red-300" : "bg-gold/10 border-gold/40 text-gold"}`}>
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg leading-tight" data-testid="confirm-title">{state.title}</h3>
                {state.body && <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed whitespace-pre-line" data-testid="confirm-body">{state.body}</p>}
              </div>
              <button onClick={() => close(false)} className="text-zinc-500 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 pb-5 flex items-center justify-end gap-2">
              <button
                data-testid="confirm-cancel"
                onClick={() => close(false)}
                className="h-9 px-3 rounded border border-white/15 hover:border-white/30 text-sm text-zinc-200"
              >
                {state.cancelLabel}
              </button>
              <button
                data-testid="confirm-accept"
                onClick={() => close(true)}
                className={`h-9 px-4 rounded text-sm font-medium ${state.tone === "danger" ? "bg-red-500 text-white hover:bg-red-500/90" : "bg-gold text-black hover:bg-gold-hover"}`}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

export default useConfirm;
